import { useCallback, useState } from 'react';
import type {
  PlacaProduct,
  PlacaQuote,
  PlacaActiveTab,
} from '../types/placas';
import * as placasService from '../services/placas.service';
import { withAuthRetry } from '../utils/withAuthRetry';

interface UsePlacasProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  externalActiveTab?: PlacaActiveTab;
}

export function usePlacas({ accessToken, refreshToken, attemptRefresh, externalActiveTab }: UsePlacasProps) {
  // Active tab (use external if provided, otherwise internal)
  const [internalActiveTab, setInternalActiveTab] = useState<PlacaActiveTab>('products');
  const activeTab = externalActiveTab ?? internalActiveTab;
  const setActiveTab = setInternalActiveTab;

  // Loading states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Data states
  const [products, setProducts] = useState<PlacaProduct[]>([]);
  const [quotes, setQuotes] = useState<PlacaQuote[]>([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<unknown>(null);

  // ===== Fetch Functions =====

  const fetchProducts = useCallback(async () => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setLoading(true);
    try {
      const data = await withAuthRetry(
        () => placasService.fetchProducts(accessToken!, { search: searchTerm, includeInactive }),
        refreshToken,
        attemptRefresh
      );
      setProducts(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar produtos' };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, searchTerm, includeInactive]);

  const fetchQuotes = useCallback(async (params?: { search?: string; status?: string }) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setLoading(true);
    try {
      const data = await withAuthRetry(
        () => placasService.fetchQuotes(accessToken!, params),
        refreshToken,
        attemptRefresh
      );
      setQuotes(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar orçamentos' };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh]);

  // ===== Fetch All Based on Active Tab =====

  const fetchData = useCallback(async () => {
    switch (activeTab) {
      case 'products':
        return fetchProducts();
      case 'quotes':
        await fetchProducts();
        return fetchQuotes();
      default:
        return { success: true };
    }
  }, [activeTab, fetchProducts, fetchQuotes]);

  // ===== CRUD Operations =====

  const createProduct = useCallback(async (data: Parameters<typeof placasService.createProduct>[0]) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setSaving(true);
    try {
      const product = await placasService.createProduct(data, accessToken);
      await fetchProducts();
      return { success: true, data: product };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao criar produto' };
    } finally {
      setSaving(false);
    }
  }, [accessToken, fetchProducts]);

  const updateProduct = useCallback(async (id: string, data: Parameters<typeof placasService.updateProduct>[1]) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setSaving(true);
    try {
      const product = await placasService.updateProduct(id, data, accessToken);
      await fetchProducts();
      return { success: true, data: product };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao atualizar produto' };
    } finally {
      setSaving(false);
    }
  }, [accessToken, fetchProducts]);

  const deleteProduct = useCallback(async (id: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      await placasService.deleteProduct(id, accessToken);
      await fetchProducts();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao excluir produto' };
    }
  }, [accessToken, fetchProducts]);

  const toggleProductStatus = useCallback(async (id: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      await placasService.toggleProductStatus(id, accessToken);
      await fetchProducts();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao alternar status' };
    }
  }, [accessToken, fetchProducts]);

  const updateQuoteStatus = useCallback(async (id: string, status: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      await placasService.updateQuoteStatus(id, status, accessToken);
      await fetchQuotes();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao atualizar status' };
    }
  }, [accessToken, fetchQuotes]);

  const markPlacaQuoteAsViewed = useCallback(async (quoteId: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };

    // Optimistically update local state
    setQuotes((prev) =>
      prev.map((q) =>
        q.id === quoteId ? { ...q, isViewed: true } : q
      )
    );

    try {
      await withAuthRetry(
        () => placasService.markPlacaQuoteAsViewed(quoteId, accessToken!),
        refreshToken,
        attemptRefresh
      );
      await fetchQuotes();
      return { success: true };
    } catch (err) {
      // Revert optimistic update on error
      setQuotes((prev) =>
        prev.map((q) =>
          q.id === quoteId ? { ...q, isViewed: false } : q
        )
      );
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao marcar como visualizado' };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchQuotes]);

  const updateQuoteItem = useCallback(async (itemId: string, data: { quantity: number }) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      const result = await withAuthRetry(
        () => placasService.updateQuoteItem(itemId, data, accessToken!),
        refreshToken,
        attemptRefresh
      );
      await fetchQuotes();
      return { success: true, data: result };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao atualizar item' };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchQuotes]);

  const deleteQuoteItem = useCallback(async (itemId: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      await withAuthRetry(
        () => placasService.deleteQuoteItem(itemId, accessToken!),
        refreshToken,
        attemptRefresh
      );
      await fetchQuotes();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao remover item' };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchQuotes]);

  const addQuoteItem = useCallback(async (quoteId: string, data: { productId: string; quantity: number; dimensionId?: string }) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      const result = await withAuthRetry(
        () => placasService.addQuoteItem(quoteId, data, accessToken!),
        refreshToken,
        attemptRefresh
      );
      await fetchQuotes();
      return { success: true, data: result };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao adicionar item' };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchQuotes]);

  return {
    // Tab
    activeTab,
    setActiveTab,

    // Loading
    loading,
    saving,

    // Data
    products,
    quotes,
    setQuotes,

    // Filters
    searchTerm,
    setSearchTerm,
    includeInactive,
    setIncludeInactive,

    // Dialog
    dialogOpen,
    setDialogOpen,
    selectedItem,
    setSelectedItem,

    // Fetch
    fetchData,
    fetchProducts,
    fetchQuotes,

    // CRUD
    createProduct,
    updateProduct,
    deleteProduct,
    toggleProductStatus,
    updateQuoteStatus,
    markPlacaQuoteAsViewed,
    updateQuoteItem,
    deleteQuoteItem,
    addQuoteItem,
  };
}
